import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Typography, Container, CssBaseline } from "@material-ui/core";
import Input from "@material-ui/core/Input";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPencilAlt } from "@fortawesome/free-solid-svg-icons";
import { emailLinkSent, forgotPasswordAgainemailLinkSent, updateEmailResendLink, updatepasswordem } from "../../services/firebase";
import { withStyles } from "@material-ui/core/styles";
import loginBg from "../../assets/images/pc_loginBg.jpg";
import cmsAdminLogo from "../../assets/images/cmsAdminLogo.png";
import { DOMAINURL, EMP_DOMAIN } from "../../services/config";
import { getAuth, sendEmailVerification, verifyPasswordResetCode } from "firebase/auth";
import { useToast } from "../../hooks/useToast";

const styles = (theme) => ({
  pageroot: {
    background: "url(" + loginBg + ") top center no-repeat",
    backgroundSize: "cover",
    backgroundAttachment: "fixed",
    height: "100vh",
    paddingTop: theme.spacing(2),
  },
  paper: {},
  avatar: {
    margin: theme.spacing(1),
    backgroundColor: theme.palette.primary.main,
  },
  form: {
    width: "100%", // Fix IE 11 issue.
    marginTop: theme.spacing(4),
  },
  submit: {
    margin: theme.spacing(4, 0, 2),
    background: "#078037",
    color: "#fff",
    "&:hover": {
      background: "#078037",
    },
  },
});

const ResetPassword = (props) => {
  let history = useNavigate();
  const queryParameters = new URLSearchParams(window.location.search);
  const oobCode = queryParameters.get("oobCode");
  const [showPassword, setShowPassword] = useState(false);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [linkValid, setLinkValid] = useState(null);
  const { toast } = useToast();
  // const [emailFromLink, setEmailFromLink] = useState("");

  const showErrorMessage = (message) => {
    setErrorMessage(message);
  };

  useEffect(() => {
    const auth = getAuth();
    const mode = queryParameters.get("mode");
    const apiKey = queryParameters.get("apiKey");
    const continueUrl = queryParameters.get("continueUrl");
    const ua = navigator.userAgent.toLowerCase();

    if (!oobCode) {
      setLinkValid(false);
      return;
    }

    // ResetPassword serves as a ROUTER component for Firebase action links
    // It validates the oobCode and determines which page should handle the action:
    // - mode=resetPassword → /updatepassword (allow user to set new password)
    // - mode=verifyEmail → /verify-email (verify user's email and activate account)
    // - mode=verifyAndChangeEmail → /verify-new-email (verify updated email)
    verifyPasswordResetCode(auth, oobCode)
      .then((emails) => {
        setLinkValid(true);
        let pathFromContinueUrl = "";
        let role = null;
        let email = null;
        let employeerole = null;
        let userId = null;
        try {
          const parsed = new URL(continueUrl);
          pathFromContinueUrl = parsed.pathname.toLowerCase();
          const innerQueryParams = new URLSearchParams(parsed.search);
          role = innerQueryParams.get("role");
          email = innerQueryParams.get("email");
          userId = innerQueryParams.get("userId");
          employeerole = innerQueryParams.get("employeerole");
        } catch {
          // continueUrl might be invalid or not provided
        }

        if (!mode || !oobCode) {
          history("/");
          return;
        }

        const query = new URLSearchParams({
          oobCode,
          apiKey,
          ...(role && { role }),
          ...(email && { email }),
          ...(employeerole && { employeerole }),
          ...(userId && { userId }),
        }).toString();

        // Route to the appropriate action handler based on mode
        if (pathFromContinueUrl === "/updatepassword" || mode === "resetPassword") {
          // Password reset flow - user will set a new password
          history(`/updatepassword?${query}`);
        } else if (pathFromContinueUrl === "/verify-new-email") {
          // Email change verification flow
          history(`/verify-new-email?${query}`);
        } else if (pathFromContinueUrl === "/verify-email" || mode === "verifyEmail") {
          // Email verification flow - this is the PRIMARY registration path
          // The oobCode will be passed to VerifyEmail which will:
          // 1. applyActionCode(oobCode) to activate the email
          // 2. Redirect to /login for the user to sign in
          history(`/verify-email?${query}`);
        } else {
          // Fallback: redirect to employee domain if unable to determine action
          window.location.href = `https://${EMP_DOMAIN}/updatepassword?${query}`;
        }
      })
      .catch((error) => {
        console.error("Invalid reset code:", error);
        setLinkValid(false);
      });

  }, [history]);

  const updatePass = async (e) => {
    e.preventDefault();
    setErrorMessage("");
    setSuccessMessage("");

    const pass = validatePassword(password);
    if (password !== confirmPassword) {
      setErrorMessage("Passwords do not match");
    } else if (pass) {
      // Call the updatepasswordem function which updates Firebase password
      await updatepasswordem(oobCode, password, () => {
        setSuccessMessage("Password Updated, Redirecting to Login...");
        
        // Use a brief timeout to show the success message to the user
        // Then do a hard redirect to /login to ensure fresh page load
        setTimeout(() => {
          window.location.replace('/login');
        }, 1500);
      },
      (resError) => {
        showErrorMessage(resError?.message || "Something Went Wrong!");
      });
    }
  };


  const handleResend = async (email) => {
    const auth = getAuth();

    try {
      // await sendEmailVerification(auth.currentUser, {
      //   url: `https://${DOMAINURL}/firebase-action-handler?email=${email}`,
      //   handleCodeInApp: true,
      // });

      await emailLinkSent(email, null);

      toast?.({
        title: "Verification Sent",
        description: `Verification email sent to ${email}`,
        variant: "default",
      });

      history("/login");

    } catch (error) {
      console.error("Error resending verification:", error);
      toast?.({
        title: "Error",
        description:
          error.code === "auth/too-many-requests"
            ? "You’ve tried too many times. Please wait before trying again."
            : "Failed to send verification email.",
        variant: "destructive",
      });
    }
  };

  const handleResendPassword = async (email) => {
    try {

      await forgotPasswordAgainemailLinkSent(email);

      toast?.({
        title: "Reset Password Sent",
        description: `Reset Password email sent to ${email}`,
        variant: "default",
      });

      history("/login");

    } catch (error) {
      console.error("Error resending reset password:", error);
      toast?.({
        title: "Error",
        description:
          error.code === "auth/too-many-requests"
            ? "You’ve tried too many times. Please wait before trying again."
            : "Failed to send reset password email.",
        variant: "destructive",
      });
    }
  };

  const handleResendSendLink = async (userId, role) => {
    try {

      await updateEmailResendLink(userId, role);

      toast?.({
        title: "Resend Email Update Verify",
        description: `Resend Email Update Verify Link Sent`,
        variant: "default",
      });

      history("/login");

    } catch (error) {
      console.error("Error resending email update link", error);
      toast?.({
        title: "Error",
        description:
          error.code === "auth/too-many-requests"
            ? "You’ve tried too many times. Please wait before trying again."
            : "Failed to send resend email update link.",
        variant: "destructive",
      });
    }
  };


  const validatePassword = (password) => {
    if (password.length < 8) {
      setErrorMessage("Password must be at least 8 characters long");
      return false;
    }
    if (!/\d/.test(password)) {
      setErrorMessage("Password must contain at least one number");
      return false;
    }
    if (!/[a-z]/.test(password)) {
      setErrorMessage("Password must contain at least one lowercase letter");
      return false;
    }
    if (!/[A-Z]/.test(password)) {
      setErrorMessage("Password must contain at least one uppercase letter");
      return false;
    }
    setErrorMessage("");
    return true;
  };
  const handleInputChange = (e) => {
    setPassword(e.target.value);
    validatePassword(e.target.value);
  };
  function handlePaste(e) {
    e.preventDefault();
  }
  const togglePasswordVisibility = () => {
    setShowPassword((prevState) => !prevState);
  };
  const { classes } = props;

  if (linkValid === null) {
    return (
      <div className={classes.pageroot}>
        <Container component="main" maxWidth="xs">
          <CssBaseline />
          <div className={`${classes.paper} loginwrap sectionBox text-center`}>
            <img className="cmslogo" src={cmsAdminLogo} alt="PROTeam Login" />
            <Typography className="text-[18px] font-medium mv-20">
              Verifying link, please wait...
            </Typography>
          </div>
        </Container>
      </div>
    );
  }

  if (linkValid === false) {
    const mode = queryParameters.get("mode");
    const continueUrl = queryParameters.get("continueUrl");
    let pageType = "resetPassword";
    let email = "";
    let userId = "";
    let role = "";
    try {
      if (continueUrl) {
        const parsed = new URL(continueUrl);
        const path = parsed.pathname;
        const innerQueryParams = new URLSearchParams(parsed.search);
        email = innerQueryParams.get("email");
        userId = innerQueryParams.get("userId");
        role = innerQueryParams.get("role");
        if (path === "/verify-email" || mode === "verifyEmail") {
          pageType = "verifyEmail";
        } else if (path === "/verify-new-email" || path === "/verifyEmpEmail") {
          pageType = "verifyNewEmail";
        }
      } else if(mode === "verifyAndChangeEmail"){
        pageType = "verifyNewEmail";
      } else if (mode === "verifyEmail") {
        pageType = "verifyEmail";
      }
    } catch (e) {
      console.error("Invalid continueUrl:", e);
    }

    return (
      <div className={classes.pageroot}>
        <Container component="main" maxWidth="xs">
          <CssBaseline />
          <div
            style={{
              minHeight: "100vh",
              background: "#f0fdf4",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              padding: "20px",
            }}
          >
            <img
              src={cmsAdminLogo}
              alt="PROTeam Logo"
              style={{
                height: "80px",
                marginBottom: "30px",
              }}
            />

            <div
              style={{
                background: "#fff",
                borderRadius: "10px",
                padding: "40px",
                boxShadow: "0 4px 15px rgba(0, 128, 0, 0.2)",
                textAlign: "center",
                maxWidth: "400px",
                width: "100%",
              }}
            >
              {pageType === "resetPassword" ? (
                <>
                  <Typography
                    style={{
                      color: "#065f46",
                      fontWeight: "600",
                      fontSize: "20px",
                      marginBottom: "20px",
                    }}
                  >
                    This link has expired or is invalid.
                  </Typography>

                  <Typography
                    style={{
                      fontSize: "16px",
                      color: "#444",
                      marginBottom: "25px",
                      lineHeight: 1.5,
                    }}
                  >
                    Your reset password link has expired.
                  </Typography>

                  <Typography
                    style={{
                      fontSize: "15px",
                      color: "#555",
                      marginBottom: "25px",
                      lineHeight: 1.6,
                    }}
                  >
                    You can request a new reset password email to{" "}
                    <span style={{ fontWeight: 700, fontSize: "16px" }}>
                      {email || "your registered email address"}
                    </span>
                    .
                  </Typography>

                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => handleResendPassword(email)}
                    style={{
                      backgroundColor: "#27ae60",
                      textTransform: "none",
                      fontWeight: "500",
                      padding: "10px 28px",
                      borderRadius: "8px",
                      boxShadow: "0 4px 10px rgba(39,174,96,0.3)",
                    }}
                  >
                    Request Reset Password
                  </Button>
                </>
              ) : pageType === "verifyNewEmail" ? (
                <>
                  <Typography
                    style={{
                      fontSize: "16px",
                      color: "#444",
                      marginBottom: "20px",
                      lineHeight: 1.5,
                    }}
                  >
                    Your email verification link has expired.
                  </Typography>

                  <Typography
                    style={{
                      fontSize: "15px",
                      color: "#555",
                      marginBottom: "25px",
                      lineHeight: 1.6,
                    }}
                  >
                    Request a new link below — we’ll send a verification email to your new address and a code to your current email.
                  </Typography>

                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => handleResendSendLink(userId, role)}
                    style={{
                      backgroundColor: "#27ae60",
                      textTransform: "none",
                      fontWeight: "500",
                      padding: "10px 28px",
                      borderRadius: "8px",
                      boxShadow: "0 4px 10px rgba(39,174,96,0.3)",
                    }}
                  >
                    Request New Verification Link
                  </Button>
                </>
              ) : (
                <>
                  <Typography
                    style={{
                      fontSize: "16px",
                      color: "#444",
                      marginBottom: "20px",
                      lineHeight: 1.5,
                    }}
                  >
                    Your email verification link has expired.
                  </Typography>

                  <Typography
                    style={{
                      fontSize: "15px",
                      color: "#555",
                      marginBottom: "25px",
                      lineHeight: 1.6,
                    }}
                  >
                    To request a new verification link for{" "}
                    <span style={{ fontWeight: 700, fontSize: "16px" }}>
                      {email || "your registered email address"}
                    </span>
                    , please click the button below.
                  </Typography>

                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => handleResend(email)}
                    style={{
                      backgroundColor: "#27ae60",
                      textTransform: "none",
                      fontWeight: "500",
                      padding: "10px 28px",
                      borderRadius: "8px",
                      boxShadow: "0 4px 10px rgba(39,174,96,0.3)",
                    }}
                  >
                    Request Verification
                  </Button>
                </>
              )}
            </div>
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div className={classes.pageroot}>

      <Container component="main" maxWidth="xs">
        <CssBaseline />
        <div
          style={{
            minHeight: "100vh",
            background: "#f0fdf4",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            padding: "20px",
          }}
        >
          <img
            src={cmsAdminLogo}
            alt="PROTeam Logo"
            style={{
              height: "80px",
              marginBottom: "30px",
            }}
          />

          <div
            style={{
              background: "#fff",
              borderRadius: "10px",
              padding: "40px",
              boxShadow: "0 4px 15px rgba(0, 128, 0, 0.2)",
              textAlign: "center",
              maxWidth: "400px",
              width: "100%",
            }}
          >
            <form noValidate>
              <Typography
                variant="h5"
                style={{
                  color: "#065f46",
                  fontWeight: "600",
                  marginBottom: "25px",
                  fontSize: "20px",
                }}
              >
                Reset Your Password
              </Typography>

              <div className="formFieldGroup" style={{ marginBottom: "20px" }}>
                <Input
                  fullWidth={true}
                  disableUnderline={true}
                  onChange={handleInputChange}
                  value={password}
                  className="input"
                  name="password"
                  id="password"
                  placeholder="Enter New Password"
                  type={showPassword ? "text" : "password"}
                  startAdornment={
                    <span className="input-adornment">
                      <FontAwesomeIcon
                        icon={faPencilAlt}
                        onClick={togglePasswordVisibility}
                      />
                    </span>
                  }
                  style={{
                    border: "1px solid #d1d5db",
                    borderRadius: "8px",
                    padding: "10px 12px",
                    marginBottom: "15px",
                  }}
                />
                <Input
                  fullWidth
                  disableUnderline
                  onChange={(event) => setConfirmPassword(event.target.value)}
                  value={confirmPassword}
                  className="input"
                  name="confrimpassword"
                  id="confrimpassword"
                  placeholder="Confirm Password"
                  type={showPassword ? "text" : "password"}
                  onPaste={handlePaste}
                  startAdornment={
                    <span className="input-adornment">
                      <FontAwesomeIcon
                        icon={faPencilAlt}
                        onClick={togglePasswordVisibility}
                      />
                    </span>
                  }
                  style={{
                    border: "1px solid #d1d5db",
                    borderRadius: "8px",
                    padding: "10px 12px",
                  }}
                />
              </div>

              <Button
                type="submit"
                fullWidth={true}
                variant="contained"
                color="primary"
                onClick={updatePass}
                style={{
                  backgroundColor: "#27ae60",
                  textTransform: "none",
                  fontWeight: "500",
                  padding: "10px 28px",
                  borderRadius: "8px",
                  boxShadow: "0 4px 10px rgba(39,174,96,0.3)",
                  marginBottom: "15px",
                }}
              >
                UPDATE PASSWORD
              </Button>

              {errorMessage && (
                <Typography color="error" variant="body2" style={{ marginBottom: "8px" }}>
                  {errorMessage}
                </Typography>
              )}
              {successMessage && (
                <Typography
                  color="primary"
                  variant="body2"
                  style={{ marginBottom: "8px" }}
                >
                  {successMessage}
                </Typography>
              )}

              <Typography
                variant="body2"
                style={{ marginTop: "15px", fontSize: "14px" }}
              >
                Already Registered?{" "}
                <a href="/login" style={{ color: "#27ae60", fontWeight: 500 }}>
                  Click here
                </a>
              </Typography>
            </form>
          </div>
          </div>
      </Container>
    </div>
  );
};

export default withStyles(styles, { withTheme: true })(ResetPassword);
